/**
 * Needs you's list: everything that needs a person, in one column, worked
 * top to bottom — each section under its name and count, grouped by
 * subject. A row says just enough to scan: an identity question its record's
 * number and statement; a search its sources and names; any other ask its
 * question; a review issue its severity, message and page. The selected row
 * stays in view as the list moves under it.
 */

import { useEffect, useRef } from 'react';

import { Alert, Badge, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import { SECTION_NAMES, sourcesOf, type WorkbenchHeader } from '@/types/workbench';
import { agentWords } from './AgentText';
import { askRecord, type NeedsYouItem, type NeedsYouSection } from './needsYouOrder';
import { listOf, textOf } from './panes/common';
import { recordName, useRecordIndex } from './recordIndex';
import classes from './NeedsYou.module.css';

function Row({ item }: Readonly<{ item: NeedsYouItem }>) {
  const records = useRecordIndex();
  if (item.kind === 'issue') {
    const { issue } = item;
    return (
      <Group gap={6} wrap="nowrap" align="flex-start">
        <Badge
          size="xs"
          color={issue.severity === 'error' ? 'red' : 'yellow'}
          variant="light"
          w={62}
          style={{ textTransform: 'none', flexShrink: 0 }}
        >
          {issue.severity === 'error' ? 'Error' : 'Warning'}
        </Badge>
        <Text size="xs" lineClamp={2} style={{ flex: 1 }}>
          {issue.message}
        </Text>
        {issue.page !== null && (
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            p.{issue.page}
          </Text>
        )}
      </Group>
    );
  }
  const { ask } = item;
  switch (ask.form ?? item.section) {
    case 'identity': {
      const entry = records.entry(askRecord(ask) ?? '');
      return (
        <Group gap={8} wrap="nowrap">
          <Text size="xs" c="dimmed" w={78} style={{ flexShrink: 0 }}>
            {recordName(entry)}
          </Text>
          <Text size="xs" truncate style={{ flex: 1 }}>
            {entry?.statement ?? textOf(ask.payload.statement) ?? ask.question}
          </Text>
        </Group>
      );
    }
    case 'search':
    case 'searches': {
      const names = listOf(ask.payload.names);
      return (
        <Text size="xs" truncate>
          {sourcesOf(ask.payload)
            .map((s) => s.name)
            .join(' · ') || ask.question}
          {names.length > 0 && (
            <Text span size="xs" c="dimmed">
              {' '}
              — for {names.join('; ')}
            </Text>
          )}
        </Text>
      );
    }
    default:
      return (
        <Text size="xs" lineClamp={2}>
          {agentWords(ask.question ?? 'An agent needs you.', records.entry)}
        </Text>
      );
  }
}

interface NeedsYouListProps {
  header: WorkbenchHeader;
  sections: NeedsYouSection[];
  selectedId: string | null;
  onSelect: (item: NeedsYouItem) => void;
  /** What agents settled while the person worked. */
  notice: string | null;
  onNoticeClose: () => void;
}

export function NeedsYouList({
  header,
  sections,
  selectedId,
  onSelect,
  notice,
  onNoticeClose,
}: Readonly<NeedsYouListProps>) {
  const selected = useRef<HTMLButtonElement | null>(null);
  const names = new Map(header.subjects.map((s) => [s.id, s.name]));

  useEffect(() => {
    selected.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedId]);

  return (
    <Stack gap="sm" data-testid="needs-you-list">
      {notice && (
        <Alert
          variant="light"
          color="blue"
          icon={<IconInfoCircle size={16} />}
          withCloseButton
          onClose={onNoticeClose}
          p={6}
          data-testid="needs-you-notice"
        >
          <Text size="xs">{notice}</Text>
        </Alert>
      )}
      {sections.map((section) => (
        <Stack key={section.section} gap={4} data-section={section.section}>
          <Group gap={6}>
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.04em' }}>
              {SECTION_NAMES[section.section]}
            </Text>
            <Badge size="xs" variant="light" color="gray">
              {section.count}
            </Badge>
          </Group>
          {section.groups.map((group) => (
            <Stack key={group.key} gap={1}>
              {section.section !== 'draft_review' &&
                (section.groups.length > 1 || group.subjectId !== null) && (
                  <Text size="xs" fw={600} mt={2} px={6}>
                    {group.subjectId ? (names.get(group.subjectId) ?? 'A subject') : 'The case'} ·{' '}
                    {group.items.length}
                  </Text>
                )}
              {group.items.map((item) => {
                const isSelected = item.id === selectedId;
                return (
                  <UnstyledButton
                    key={item.id}
                    ref={isSelected ? selected : undefined}
                    onClick={() => onSelect(item)}
                    className={classes.row}
                    aria-current={isSelected}
                    data-testid={`needs-you-item-${item.id}`}
                  >
                    <Row item={item} />
                  </UnstyledButton>
                );
              })}
            </Stack>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
